/**
 * Invoice preview dialog — mirrors the quotation preview.
 *
 * Opens a modal with an iframe rendering the invoice (GET .../render, which is
 * watermarked while the invoice is awaiting approval), so staff can review the
 * document before it is approved or sent. From the dialog they can print / save
 * the PDF or send it to the customer — both only once it is approved. An invoice
 * needs approval when it is corporate or was raised by anyone other than root /
 * finance_manager.
 */
import { openDialog, closeDialog } from '../../src/lib/dialog.js';
import { escapeHtml } from '../../src/lib/format.js';
import { hasRole } from '../../src/lib/roles.js';
import * as api from '../api-client.js';
import * as notify from '../notifications.js';

const API = api.routes;
const flash = (m, t) => notify.showFlash(m, t);
const EDIT_ROLES = ['root', 'sales_officer', 'sales_supervisor', 'billing_officer', 'billing_supervisor'];
// Approval gate — matches the server-side `_SUP` role set.
const APPROVE_ROLES = ['root', 'finance_manager', 'sales_supervisor', 'billing_supervisor'];

export function openInvoicePreview(inv, { onChanged } = {}) {
  if (!inv || !inv.id) return;
  const awaiting = !!(inv.pending_approval || inv.status === 'awaiting_approval');
  const isReceipt = inv.kind === 'receipt';
  const canSend = hasRole(...EDIT_ROLES) && !isReceipt && inv.status === 'issued' && !awaiting;
  // Approve/Reject only when the server reports a real decidable gate — an invoice
  // can look "awaiting" without one (already decided, figures changed), and deciding
  // then 404s with "no_pending_gate". Fall back to `awaiting` for older API responses
  // that don't send `pending_gate`.
  const hasPendingGate = inv.pending_gate != null ? !!inv.pending_gate : awaiting;
  const canApprove = hasPendingGate && !isReceipt && hasRole(...APPROVE_ROLES);
  // An unsent (not sent, not void) invoice can be corrected in place, without void+recreate.
  const canAdjust = hasRole(...EDIT_ROLES) && !isReceipt
    && inv.status !== 'sent' && inv.status !== 'void';
  // Re-generate rebuilds an unsent invoice from the customer's current profile and
  // billing (e.g. after refreshing the customer), keeping its number.
  const canRegenerate = hasRole(...EDIT_ROLES) && !isReceipt
    && (inv.status === 'issued' || inv.status === 'awaiting_approval');

  const dlg = document.createElement('dialog');
  dlg.className = 'modal invoice-preview-modal';
  dlg.style.cssText = 'width:min(920px,95vw); max-width:95vw; padding:0; border:none; border-radius:12px; background:var(--panel,#fff);';
  dlg.innerHTML = `
    <div style="display:flex; flex-direction:column; height:min(85vh,900px);">
      <div class="crm-detail-header" style="padding:12px 16px; border-bottom:1px solid var(--border-soft); display:flex; align-items:center; gap:8px;">
        <strong>${isReceipt ? 'Receipt' : 'Invoice'} ${escapeHtml(inv.number || '')}</strong>
        <span class="badge ${awaiting ? 'warn' : 'ok'}">${awaiting ? 'Awaiting approval' : escapeHtml(inv.status || '')}</span>
        ${canRegenerate ? '<button class="btn ghost" data-regenerate style="margin-left:auto;" title="Rebuild this invoice from the customer\'s current profile and billing. The number stays the same.">↻ Re-generate</button>' : ''}
        <button class="btn ghost" data-details${canRegenerate ? '' : ' style="margin-left:auto;"'}>Details ▾</button>
        <button class="btn ghost" data-close>✕</button>
      </div>
      <div data-details-panel hidden style="padding:8px 16px; border-bottom:1px solid var(--border-soft); background:var(--panel-2,#f8fafc); font-size:12px;"></div>
      <div data-adjust-panel hidden style="padding:10px 16px; border-bottom:1px solid var(--border-soft); background:var(--panel-2,#f8fafc); font-size:12px;">
        <div style="font-weight:600; margin-bottom:6px;">Adjust amounts <span class="crm-detail-sub">(goes back for approval unless you are root or a finance manager, and always for corporate)</span></div>
        <div style="display:flex; gap:10px; flex-wrap:wrap; align-items:flex-end;">
          <label class="field" style="flex:2; min-width:180px;"><span class="crm-detail-sub">Line description (optional)</span><input data-adj-desc type="text" placeholder="Keep current" /></label>
          <label class="field" style="min-width:120px;"><span class="crm-detail-sub">This period</span><input data-adj-tp type="number" step="0.01" min="0" /></label>
          <label class="field" style="min-width:120px;"><span class="crm-detail-sub">Overdue</span><input data-adj-ov type="number" step="0.01" min="0" /></label>
          <label class="field" style="min-width:90px;"><span class="crm-detail-sub">VAT %</span><input data-adj-vat type="number" step="0.01" min="0" max="100" placeholder="0" title="VAT on the net subscription charges only — PortaBilling balances already include VAT" /></label>
          <label class="field" style="min-width:150px;"><span class="crm-detail-sub">VAT shown</span><select data-adj-vat-mode title="Added on top: a separate VAT row above the total. Included in prices: the VAT is folded into the line amounts. The total due is the same either way."><option value="exclusive">Added on top</option><option value="inclusive">Included in prices</option></select></label>
          <label class="field" style="flex:2; min-width:180px;"><span class="crm-detail-sub">Reason (optional)</span><input data-adj-note type="text" placeholder="e.g. one-off waiver" /></label>
          <button class="btn primary" data-adj-save>Save</button>
          <button class="btn ghost" data-adj-cancel>Cancel</button>
        </div>
        <div style="margin-top:8px; padding-top:8px; border-top:1px dashed var(--border-soft); display:flex; gap:16px; align-items:center; flex-wrap:wrap;">
          <label style="display:inline-flex; align-items:center; gap:6px;">
            <input type="checkbox" data-adj-expand> Itemise each connection (expanded layout)
          </label>
          <label style="display:inline-flex; align-items:center; gap:6px;" title="How many months this invoice bills — the whole service period (1 = just the first month)">
            Months to bill
            <input data-adj-months type="number" min="1" max="60" step="1" class="input" style="width:72px;" placeholder="3" />
          </label>
          <span class="crm-detail-sub">Changing either refreshes the invoice from PortaBilling — the service period and figures are recomputed.</span>
        </div>
        <div style="margin-top:8px; padding-top:8px; border-top:1px dashed var(--border-soft);">
          <button type="button" class="btn ghost" data-adj-wording-toggle style="padding:2px 10px;">✎ Description wording…</button>
          <span class="crm-detail-sub" style="margin-left:8px;">Wording for this customer’s invoice descriptions — saved against the customer and reused on their future invoices.</span>
          <div data-adj-wording hidden style="margin-top:8px;"></div>
        </div>
      </div>
      <iframe data-preview title="Invoice preview" style="flex:1; width:100%; border:0; background:#fff;"></iframe>
      <div style="display:flex; align-items:center; gap:8px; padding:12px 16px; border-top:1px solid var(--border-soft); flex-wrap:wrap;">
        ${awaiting && !canApprove ? '<span class="crm-detail-sub">Awaiting approval — an approver must clear it in the review console before it can be sent or downloaded.</span>' : ''}
        ${canAdjust ? '<button class="btn" data-adjust style="margin-left:auto;">Adjust</button>' : ''}
        <button class="btn" data-pdf ${awaiting ? 'disabled' : ''} style="${canAdjust ? '' : 'margin-left:auto;'}" title="${awaiting ? 'Not available until approved' : 'Print or save the invoice as a PDF'}">Print / PDF</button>
        ${canApprove ? '<button class="btn ghost" data-reject>Reject</button><button class="btn primary" data-approve>Approve</button>' : ''}
        <button class="btn primary" data-send ${canSend ? '' : 'disabled'} title="${canSend ? 'Email this invoice to the customer' : 'Not available until approved / already sent'}"${canApprove ? ' style="display:none;"' : ''}>Send to customer</button>
      </div>
    </div>`;
  document.body.appendChild(dlg);

  const cleanup = () => { try { closeDialog(dlg); } catch { /* noop */ } dlg.remove(); };
  dlg.addEventListener('close', () => dlg.remove());   // native Esc / backdrop close
  dlg.querySelector('[data-preview]').src = `${API.invoicingRender(inv.id)}?t=${Date.now()}`;
  dlg.querySelector('[data-close]').addEventListener('click', cleanup);
  // "Print / PDF": open the printable HTML render and trigger the browser's print
  // dialog (Save as PDF). Works even where the host has no server-side PDF engine
  // (the cause of the production `pdf_unavailable` error).
  dlg.querySelector('[data-pdf]').addEventListener('click', () => {
    if (awaiting) return;   // the server refuses the printable copy until approval too
    const w = window.open(`${API.invoicingRender(inv.id)}?print=1&t=${Date.now()}`, '_blank');
    if (w) { try { w.addEventListener('load', () => setTimeout(() => { try { w.print(); } catch { /* noop */ } }, 350)); } catch { /* noop */ } }
  });

  if (canRegenerate) {
    const regenBtn = dlg.querySelector('[data-regenerate]');
    regenBtn.addEventListener('click', async () => {
      if (!window.confirm('Re-generate this invoice from the customer\'s current details and billing?\n\n'
        + 'It keeps the same number and is dated today. If the amounts change, it needs approval again.')) return;
      regenBtn.disabled = true;
      regenBtn.textContent = 'Re-generating…';
      try {
        const updated = await api.fetch(API.invoicingRegenerate(inv.id), { method: 'POST' });
        const nowAwaiting = !!(updated && updated.pending_approval);
        flash(nowAwaiting && !awaiting
          ? 'Invoice re-generated — the amounts changed, so it needs approval again.'
          : (nowAwaiting ? 'Invoice re-generated — still awaiting approval.' : 'Invoice re-generated.'), 'success');
        // Reopen on the fresh invoice so the status, Send and Print / PDF reflect it.
        cleanup();
        if (onChanged) onChanged();
        openInvoicePreview(updated, { onChanged });
      } catch (err) {
        regenBtn.disabled = false;
        regenBtn.textContent = '↻ Re-generate';
        flash(api.formatErrorDetail(err && err.body && err.body.detail) || 'Could not re-generate the invoice.', 'error');
      }
    });
  }

  // Details panel: who created / approved / sent the invoice (lazy-fetched once).
  const detailsBtn = dlg.querySelector('[data-details]');
  const detailsPanel = dlg.querySelector('[data-details-panel]');
  let detailsLoaded = false;
  const fmtWhen = (iso) => { try { return iso ? new Date(iso).toLocaleString() : '—'; } catch { return iso || '—'; } };
  detailsBtn.addEventListener('click', async () => {
    const show = detailsPanel.hidden;
    detailsPanel.hidden = !show;
    detailsBtn.textContent = show ? 'Details ▴' : 'Details ▾';
    if (show && !detailsLoaded) {
      detailsLoaded = true;
      detailsPanel.innerHTML = '<span class="crm-detail-sub">Loading…</span>';
      try {
        const full = await api.fetch(API.invoicingInvoice(inv.id), { method: 'GET' });
        const row = (label, val) => val ? `<div style="display:flex; gap:8px;"><span class="crm-detail-sub" style="min-width:110px;">${label}</span><span>${escapeHtml(val)}</span></div>` : '';
        const appr = full.approval;
        detailsPanel.innerHTML = [
          row('Created by', full.created_by),
          appr ? row('Approved by', `${appr.by || '—'}${appr.at ? ' · ' + fmtWhen(appr.at) : ''}${appr.note ? ' · “' + appr.note + '”' : ''}`) : '',
          full.sent_at ? row('Sent by', `${full.sent_by || '—'} · ${fmtWhen(full.sent_at)}`) : row('Sent', 'Not sent yet'),
        ].filter(Boolean).join('') || '<span class="crm-detail-sub">No activity yet.</span>';
      } catch {
        detailsPanel.innerHTML = '<span class="crm-detail-error">Could not load details.</span>';
        detailsLoaded = false;
      }
    }
  });

  if (canAdjust) {
    const adjustBtn = dlg.querySelector('[data-adjust]');
    const adjustPanel = dlg.querySelector('[data-adjust-panel]');
    const reloadPreview = () => { dlg.querySelector('[data-preview]').src = `${API.invoicingRender(inv.id)}?t=${Date.now()}`; };
    const expandBox = adjustPanel.querySelector('[data-adj-expand]');
    const monthsInput = adjustPanel.querySelector('[data-adj-months]');
    let initialItemized = true;   // the invoice's current layout, set on first open
    let initialVatMode = 'exclusive';   // the invoice's current VAT presentation
    let initialMonths = null;     // the invoice's current billing span, set on first open
    let wordingState = null;      // {current, defaults, placeholders} for this customer
    let prefilled = false;
    adjustBtn.addEventListener('click', async () => {
      const show = adjustPanel.hidden;
      adjustPanel.hidden = !show;
      if (show && !prefilled) {
        prefilled = true;
        try {
          const full = await api.fetch(API.invoicingInvoice(inv.id), { method: 'GET' });
          if (full.subtotal != null) adjustPanel.querySelector('[data-adj-tp]').value = full.subtotal;
          if (full.overdue != null) adjustPanel.querySelector('[data-adj-ov]').value = full.overdue;
          if (full.vat_rate != null) adjustPanel.querySelector('[data-adj-vat]').value = full.vat_rate;
          if (full.vat_mode) {
            initialVatMode = full.vat_mode;
            adjustPanel.querySelector('[data-adj-vat-mode]').value = full.vat_mode;
          }
          initialItemized = full.itemized !== false;
          if (expandBox) expandBox.checked = initialItemized;
          initialMonths = full.months == null ? null : Number(full.months);
          if (monthsInput && initialMonths != null) monthsInput.value = String(initialMonths);
          wordingState = {
            current: full.line_templates || {},
            defaults: full.line_template_defaults || {},
            placeholders: full.line_template_placeholders || {},
            items: full.line_items || [],
          };
        } catch { /* leave blank */ }
      }
    });
    const wordingBox = adjustPanel.querySelector('[data-adj-wording]');
    const wordingToggle = adjustPanel.querySelector('[data-adj-wording-toggle]');
    if (wordingToggle && wordingBox) {
      wordingToggle.addEventListener('click', async () => {
        wordingBox.hidden = !wordingBox.hidden;
        if (wordingBox.hidden) return;
        if (!wordingState) {
          // The prefill runs once; if it failed there is still no wording here, so
          // retry rather than leaving the editor unusable.
          wordingBox.innerHTML = '<span class="crm-detail-sub">Loading wording…</span>';
          try {
            const full = await api.fetch(API.invoicingInvoice(inv.id), { method: 'GET' });
            wordingState = {
              current: full.line_templates || {},
              defaults: full.line_template_defaults || {},
              placeholders: full.line_template_placeholders || {},
              items: full.line_items || [],
            };
          } catch { /* fall through: render empty, editable fields */ }
        }
        renderWordingFields(wordingBox, wordingState);
      });
    }
    adjustPanel.querySelector('[data-adj-cancel]').addEventListener('click', () => { adjustPanel.hidden = true; });
    adjustPanel.querySelector('[data-adj-save]').addEventListener('click', async (ev) => {
      const btn = ev.currentTarget;
      const tp = adjustPanel.querySelector('[data-adj-tp]').value;
      const ov = adjustPanel.querySelector('[data-adj-ov]').value;
      const vat = adjustPanel.querySelector('[data-adj-vat]').value;
      const vatMode = adjustPanel.querySelector('[data-adj-vat-mode]').value;
      const vatModeChanged = vatMode !== initialVatMode;
      const desc = adjustPanel.querySelector('[data-adj-desc]').value.trim();
      const note = adjustPanel.querySelector('[data-adj-note]').value.trim();
      const body = {};
      // A layout or billing-span change refreshes the invoice from PortaBilling (period
      // and figures recomputed) — these take precedence over, and are mutually exclusive
      // with, a manual figure patch.
      const monthsVal = monthsInput && monthsInput.value !== '' ? parseInt(monthsInput.value, 10) : null;
      const layoutChanged = expandBox && expandBox.checked !== initialItemized;
      const monthsChanged = monthsVal != null && monthsVal >= 1 && monthsVal !== initialMonths;
      const wordingEdits = collectWordingEdits(wordingBox, wordingState);
      const wordingChanged = wordingEdits !== null;
      if (layoutChanged || monthsChanged || wordingChanged) {
        const bits = [];
        if (layoutChanged) bits.push(`the ${expandBox.checked ? 'expanded (per-connection)' : 'collapsed (single line)'} layout`);
        if (monthsChanged) bits.push(`a ${monthsVal}-month billing period`);
        if (wordingChanged) bits.push('new description wording (saved for this customer)');
        if (vatModeChanged) bits.push(`VAT ${vatMode === 'inclusive' ? 'included in the prices' : 'added on top'}`);
        if (!window.confirm(`Apply ${bits.join(' and ')}? The invoice will be refreshed from PortaBilling and re-rendered.`)) return;
        if (layoutChanged) body.expand_connections = expandBox.checked;
        if (monthsChanged) body.months = monthsVal;
        if (wordingChanged) body.line_templates = wordingEdits;
        // A refresh rebuilds from Porta, so carry the chosen presentation over too.
        if (vatModeChanged) body.vat_mode = vatMode;
      } else {
        if (tp !== '') body.this_period = tp;
        if (ov !== '') body.overdue = ov;
        if (vat !== '') body.vat_rate = vat;
        if (vatModeChanged) body.vat_mode = vatMode;
        if (desc) body.line_desc = desc;
        if (note) body.note = note;
        if (!Object.keys(body).length) { flash('Nothing to change.', 'info'); return; }
      }
      btn.disabled = true;
      try {
        await api.fetch(API.invoicingAdjust(inv.id), { method: 'POST', body: JSON.stringify(body) });
        const recomputed = layoutChanged || monthsChanged || wordingChanged;
        if (wordingChanged) {
          wordingState = wordingState || { current: {}, defaults: {}, placeholders: {} };
          wordingState.current = { ...wordingState.current, ...wordingEdits };
        }
        flash(recomputed ? 'Invoice updated.' : 'Invoice adjusted.', 'success');
        if (layoutChanged) initialItemized = expandBox.checked;   // keep in sync for a further toggle
        if (vatModeChanged) initialVatMode = vatMode;
        if (monthsChanged) initialMonths = monthsVal;
        adjustPanel.hidden = true;
        reloadPreview();
        if (onChanged) onChanged();
      } catch (err) {
        btn.disabled = false;
        flash(api.formatErrorDetail(err && err.body && err.body.detail) || 'Could not adjust the invoice.', 'error');
      }
    });
  }

  if (canApprove) {
    const decide = async (approve) => {
      let note = '';
      if (!approve) { note = window.prompt('Reason for rejecting (required):', '') || ''; if (!note.trim()) return; }
      else { note = window.prompt('Approval note (optional):', '') || ''; }
      try {
        await api.fetch(API.invoicingDecide(inv.id), {
          method: 'POST', body: JSON.stringify({ decision: approve ? 'approve' : 'reject', note: note || null }),
        });
        flash(approve ? 'Invoice approved.' : 'Invoice rejected.', 'success');
        cleanup();
        if (onChanged) onChanged();
      } catch (err) {
        flash(api.formatErrorDetail(err && err.body && err.body.detail) || 'Decision failed.', 'error');
      }
    };
    dlg.querySelector('[data-approve]').addEventListener('click', () => decide(true));
    dlg.querySelector('[data-reject]').addEventListener('click', () => decide(false));
  }

  const sendBtn = dlg.querySelector('[data-send]');
  if (canSend) {
    sendBtn.addEventListener('click', async () => {
      if (!window.confirm('Send this invoice to the customer now?')) return;
      sendBtn.disabled = true;
      try {
        const res = await api.fetch(API.invoicingSend(inv.id), { method: 'POST' });
        flash(`Invoice ${res && res.number ? res.number + ' ' : ''}sent to the customer.`, 'success');
        cleanup();
        if (onChanged) onChanged();
      } catch (err) {
        sendBtn.disabled = false;
        flash(api.formatErrorDetail(err && err.body && err.body.detail) || 'Could not send the invoice.', 'error');
      }
    });
  }

  openDialog(dlg);
}

export default { openInvoicePreview };


// --- per-customer description wording ---------------------------------------
// The invoice description column is template-driven. These are stored against the
// CUSTOMER, so editing them here also sets the wording for their future invoices.
const SHARED_WORDING_FIELDS = [
  ['item_desc', 'Default item description'],
  ['item_sub', 'Sub-line under each item'],
  ['connection_label', 'Connection label (itemised view)'],
];

function renderWordingFields(box, state) {
  if (!box) return;
  // Never a dead end: if we could not load this invoice's wording the fields still
  // render, so wording can be ADDED. Blank falls back to the default on the server.
  state = state || {};
  const current = state.current || {};
  const defaults = state.defaults || {};
  const placeholders = state.placeholders || {};
  const items = Array.isArray(state.items) ? state.items.filter((i) => i && i.template_key) : [];

  // A description field per line actually on this invoice.
  const itemRows = items.map((item) => `
    <label class="field" style="display:block; margin-bottom:6px;">
      <span class="crm-detail-sub">${escapeHtml(item.key || 'Item')}</span>
      <input class="input" data-wk="${escapeHtml(item.template_key)}" type="text" style="width:100%;"
             value="${escapeHtml(current[item.template_key] || '')}"
             placeholder="${escapeHtml(item.description || 'Uses the default wording')}">
    </label>`).join('');
  const itemsBlock = itemRows
    ? `<div class="crm-detail-sub" style="font-size:11px; margin-bottom:4px;">Description per item (${items.length}) — blank uses the default shown.</div>
       <div style="display:grid; grid-template-columns:repeat(auto-fit, minmax(280px,1fr)); gap:0 14px;">${itemRows}</div>`
    : '<div class="crm-detail-sub" style="font-size:11px;">No invoice items to describe yet.</div>';

  // Shared wording that applies to every item.
  const sharedRows = SHARED_WORDING_FIELDS.map(([key, label]) => `
    <label class="field" style="display:block; margin-bottom:6px;">
      <span class="crm-detail-sub">${escapeHtml(label)}</span>
      <input class="input" data-wk="${escapeHtml(key)}" type="text" style="width:100%;"
             value="${escapeHtml(current[key] || '')}"
             placeholder="${escapeHtml(defaults[key] || 'Uses the default wording')}">
    </label>`).join('');
  const tokens = Object.keys(placeholders).map((t) => `<code>${escapeHtml(t)}</code>`).join(' ');

  box.innerHTML = `
    ${itemsBlock}
    <div class="crm-detail-sub" style="font-size:11px; margin:10px 0 4px;">Applies to every item</div>
    <div style="display:grid; grid-template-columns:repeat(auto-fit, minmax(280px,1fr)); gap:0 14px;">${sharedRows}</div>
    ${tokens ? `<div class="crm-detail-sub" style="font-size:11px;">Placeholders: ${tokens}</div>` : ''}`;
}

// The edited wording, or null when nothing changed. Blank fields are sent as "" so
// the backend clears that override back to the built-in default.
function collectWordingEdits(box, state) {
  if (!box || box.hidden) return null;
  const inputs = box.querySelectorAll('[data-wk]');
  if (!inputs.length) return null;
  // No loaded state is fine — treat it as "nothing set yet" so typed wording still saves.
  const current = (state && state.current) || {};
  const out = {};
  let changed = false;
  inputs.forEach((i) => {
    const key = i.dataset.wk;
    const value = i.value.trim();
    out[key] = value;
    if (value !== (current[key] || '')) changed = true;
  });
  return changed ? out : null;
}
